<template>
    <div class="container-fluid" style="padding: 0;">
        <div class="row">
            <div class="col">
                    <hr class="labeled" data-content="Person">

                    <div class="lead-pane-field">
                        <keen-basic-contact-input
                            ref="contactInput"
                            v-model="contactProxy"
                            :invalid="errors.contact.isInvalid"
                            :name-error="errors.name.firstError"
                            :primary-telephone-error="errors.primary_telephone.firstError"
                            :email-error="errors.email.firstError"
                        />
                    </div>

                    <div class="lead-pane-field">
                        <keen-referral-source-picker
                            placeholder="Source"
                            v-model="referralProxy"
                            icon="directions"
                            :invalid="errors.referral_source_id.isInvalid"
                            :error="errors.referral_source_id.firstError"
                        />
                    </div>

                <div class="lead-pane-field" style="margin-bottom: 1.5rem;">
                    <div v-if="isSalesLead">
                        <lead-sales-sub-pane
                            :value="{ vehiclesOfInterest, vehiclesOffered }"
                            @removed="removed"
                        ></lead-sales-sub-pane>
                    </div>
                    <div v-if="isBuyingLead">
                        <lead-buying-sub-pane
                            :value="{ vehiclesOfInterest, vehiclesOffered }"
                            :lead="lead"
                            @removed="removed"
                        ></lead-buying-sub-pane>
                    </div>
                </div>
                </div>
            <div class="col">

                <div class="lead-pane-field" style="margin-bottom: 1rem;">
                    <new-lead-pipeline-stage-control
                        ref="stage"
                        :default-lead-type="lead.type"
                        @input="pipelineUpdated"
                    ></new-lead-pipeline-stage-control>
                </div>
                <keen-team-member-picker icon="face" v-model="ownerProxy"></keen-team-member-picker>
                <keen-location-picker icon="map_marker" v-model="locationProxy"></keen-location-picker>

                <div class="lead-pane-notes">
                    <hr class="labeled" data-content="Notes">
                    <div class="lead-pane-notes-container">
                        <note-pane v-model="note" :owners="noteOwners" ref="notes"></note-pane>
                    </div>
                </div>
            </div>
        </div>

        <similar-contact-modal
                ref="duplicates"
        ></similar-contact-modal>
    </div>
</template>

<style lang="scss">
    .lead-pane-field {
        padding: .25rem 0;
        .ui-textbox-larger input {
            font-size: 1.3rem;
            height: 3rem;
        }
    }

    .lead-pane-notes {
        margin-top: 2rem;
        .lead-pane-notes-container {
            background: #fffcdc;
            padding: 1rem 1rem 3rem;
            min-height: 100px;

        .ql-toolbar {
          position: absolute;
          bottom: .5rem;
          width: 100%;
          transform: translateY(100%);
        }

        .ql-toolbar, .ql-container {
            border: none;
        }

        }
    }
</style>

<script>
import {
    LeadDetailResource,
    LeadDetailTypes,
    LeadResource,
    LeadSchema,
    LeadTypesList
} from '../../classes/Api/Lead'
    import { ContactResource, ContactTypes } from '../../classes/Api/Contact'
    import ErrorHelpers from "./ErrorHelpers"
import ActivitiesPane from "../inbox/ActivitiesPane"
import ActivityPane from "./ActivityPane"
import NotePane from "./NotePane"
import LeadSalesSubPane from "./LeadSalesSubPane"
import LeadBuyingSubPane from "./LeadBuyingSubPane"
import {NoteResource} from "../../classes/Api/Note";
import SimilarContactModal from "../elements/SimilarContactModal.vue";
import cloneDeep from "lodash/cloneDeep";
import DealerKit from "../../api";
import {LeadType} from "../../../../api-clients/ts";
import NewLeadPipelineStageControl from "../lead/NewLeadPipelineStageControl.vue";

    const ErrorKeys = [
        'type_id',
        'contact_id',
        {
          key: 'contact',
          composition: [
              'first_name',
              'last_name',
              'company_name',
              'contact_id',
              'primary_telephone',
              'email'
          ]
        },
        {
            key: 'name',
            composition: [
                'first_name',
                'last_name',
                'company_name'
            ]
        },
        'primary_telephone',
        'email',
        'detail',
        'referral_source_id',
        'title',
        'owner_id',
        'location_id'
    ]

    export default {
        mixins: [ErrorHelpers],
        components: {
            NewLeadPipelineStageControl,
            SimilarContactModal,
            LeadBuyingSubPane,
            LeadSalesSubPane,
            NotePane,
            ActivityPane,
            ActivitiesPane
        },
        props: {
            defaults: {
                type: Object,
                default() {
                    return {}
                }
            }
        },
        mounted() {
            this.reset()
        },
        data() {
            return {
                isSaving: false,
                lead: {},
                note: '',
                showHiddenFields: false,
                ErrorKeys,
                LeadTypesList,
                LeadType,
                vehiclesOfInterest: [],
                vehiclesOffered: [],
            }
        },
        methods: {
            pipelineUpdated(stage) {
                this.lead.stage_id = stage.id
                this.lead.type = stage.pipeline.lead_type
            },
            removed(e) {
                if (e.type === LeadDetailTypes.VEHICLE_OF_INTEREST) {
                    this.vehiclesOfInterest = this.vehiclesOfInterest.filter(vehicleOfInterest => {
                        return vehicleOfInterest.id !== e.vehicle.id
                    })
                } else if(e.type === LeadDetailTypes.VEHICLE_OFFERED) {
                    this.vehiclesOffered = this.vehiclesOffered.filter(vehicleOffered => {
                        return vehicleOffered.vehicle.id !== e.vehicle_offered.vehicle.id
                    })
                }
            },
            setLead(lead) {
                lead = cloneDeep(lead)

                let defaults = LeadSchema(this.defaults)

                this.$set(this, 'lead', Object.assign(defaults, lead))

                if (this.$refs.stage) {
                    this.$nextTick(() => {
                        this.$refs.stage.defaultPipelineAndStage()
                    })
                }

                if (defaults.vehicles_of_interest) {
                    this.vehiclesOfInterest = defaults.vehicles_of_interest
                }
                
                if (defaults.vehicles_offered) {
                    this.vehiclesOffered = defaults.vehicles_offered
                }
            },
            reset() {
                this.showHiddenFields = false

                this.errors = this.makeErrorStack()

                this.vehiclesOffered = []
                this.vehiclesOfInterest = []

                this.isSaving = false

                this.$nextTick(() => {
                    if (this.$refs.contactInput) {
                        this.$refs.contactInput.reset()
                    }

                    this.setLead({})
                })
            },
            save() {
                this.errors = this.makeErrorStack()

                this.isSaving = true

                return this.$refs.duplicates.check(this.lead.contact).then(contact => {
                    let data = cloneDeep(this.lead)

                    this.$emit('saving', data)

                    let promises = []

                    // Save the contact.
                    if (!data.contact_id) {
                        data.contact.referral_source_id = data.referral_source_id
                        data.contact.type = ContactTypes.LEAD

                        promises.push(ContactResource.store(data.contact).then(response => {
                            this.lead.contact_id = data.contact_id = response.data.id
                            this.lead.contact = response.data
                        }).catch(e => {
                            this.handleHttpException(e, true)

                            this.isSaving = false
                        }))
                    }

                    return Promise.all(promises).then(() => { this.finishSave(data) })
                }).finally(() => { this.isSaving = false })
            },
            finishSave(data) {
                if (!data.title) {
                    data.title = this.titlePlaceholder
                }

                // return LeadResource.storeOrUpdate(this.lead.id, data).then((response) => {
                return DealerKit.leads.storeLead({
                    type: data.type,
                    referral_source_id: data.referral_source_id,
                    contact_id: data.contact_id,
                    sales_person_id: data.owner_id,
                    location_id: data.location_id,
                    conversation_id: data.conversation_id,
                    stage_id: data.stage_id
                }).then((response) => {
                    this.$set(this.lead, 'id', response.data.data.id)

                    const leadDetailQueue = []

                    this.vehiclesOfInterest.forEach((voa) => {
                        leadDetailQueue.push(DealerKit.leads.storeVehicleOfInterest(response.data.data.id, {
                            vehicle_version_id: voa.id
                        }))
                    })

                    this.vehiclesOffered.forEach((voa) => {
                        leadDetailQueue.push(DealerKit.appraisals.storeVehicleAppraisal({
                            vehicle_id: voa.vehicle.id,
                            condition: voa.condition,
                            outstanding_finance_amount: voa.outstanding_finance_amount,
                            outstanding_finance_provider: voa.outstanding_finance_provider,
                            service_history: voa.service_history,
                            appraised_value: voa.appraised_value,
                            lead_id: response.data.data.id
                        }))
                    })

                    if (this.note && this.note.length > 0) {
                        leadDetailQueue.push(DealerKit.contacts.createContactNote({
                            content: this.note,
                            contact_id: data.contact_id,
                        }))
                    }

                    Promise.all(leadDetailQueue).then(() => {
                        this.isSaving = false
                        this.$emit('saved', response.data.data)
                    }).catch(handleApiError)

                }).catch(e => {
                    this.isSaving = false

                    this.handleHttpException(e, true)
                })
            },
        },
        computed: {
            isSalesLead() {
                return this.lead.type && this.lead.type === LeadType.Sales
            },
            isBuyingLead() {
                return this.lead.type && this.lead.type === LeadType.Buying
            },
            noteOwners() {
                return [
                    { type: 'contact', id: this.lead.contact_id },
                    { type: 'lead', id: this.lead.id },
                ]
            },
            typeProxy: {
                get() {
                    return this.lead.type ? LeadTypesList.find(i => this.lead.type === i.id) : {}
                },
                set(value) {
                    this.lead.type = value.id

                    this.lead.detail = []
                }
            },
            contactProxy: {
                get() {
                    return this.lead ? this.lead.contact : {}
                },
                set(value) {
                    this.lead.contact = value
                    this.lead.contact_id = value ? value.id : null
                }
            },
            locationProxy: {
                get() {
                    return this.lead.location
                },
                set(value) {
                    this.lead.location = value
                    this.lead.location_id = value ? value.id : null
                }
            },
            referralProxy: {
                get() {
                    return this.lead.referral_source ? this.lead.referral_source : {}
                },
                set(value) {
                    this.lead.referral_source = value
                    this.lead.referral_source_id = value ? value.id : null
                }
            },
            ownerProxy: {
                get() {
                    return this.lead.owner
                },
                set(value) {
                    this.lead.owner = value
                    this.lead.owner_id = value ? value.id : null
                }
            },
        }
    }
</script>